/**
 * OAuthAuthorizePage — Claude / MCP Authorization Gate
 *
 * Mounted at /oauth/authorize. The MCP discovery doc points here.
 *
 * Silent flow (already logged in):
 *   1. Read API key from localStorage
 *   2. POST /v2/oauth/code → receive single-use code
 *   3. Redirect Claude back to redirect_uri?code=…&state=…
 *
 * Not logged in flow:
 *   1. Save OAuth params to sessionStorage
 *   2. Navigate to /login — after magic link auth, MagicLinkPage
 *      picks up savedParams and returns here to complete the silent flow.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { useAuth } from '@/contexts/AuthContext';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Key used to persist OAuth params across the login redirect */
export const OAUTH_PENDING_KEY = 'guardrail:oauth:pending';

interface OAuthParams {
  redirectUri: string;
  state: string;
  clientId: string;
}

/**
 * Parse OAuth params from the URL query string first, then fall back to
 * sessionStorage (for post-login resume where the URL no longer has them).
 */
function parseParams(): OAuthParams {
  const p = new URLSearchParams(window.location.search);
  const fromUrl: OAuthParams = {
    redirectUri: p.get('redirect_uri') ?? '',
    state: p.get('state') ?? '',
    clientId: p.get('client_id') ?? '',
  };

  // If we have a redirect_uri in the URL, that's canonical — use it.
  if (fromUrl.redirectUri) return fromUrl;

  // Otherwise try to restore from sessionStorage (post-login resume).
  try {
    const saved = sessionStorage.getItem(OAUTH_PENDING_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as OAuthParams;
      if (parsed.redirectUri) return parsed;
    }
  } catch { /* ignore parse errors */ }

  return fromUrl;
}

export default function OAuthAuthorizePage() {
  useDocumentTitle('Authorize');
  const navigate = useNavigate();
  const { authenticated, loading, token, apiKey } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for AuthContext to finish initialising before acting.
    if (loading) return;

    (async () => {
      const params = parseParams();

      if (!params.redirectUri) {
        setError('Missing redirect_uri — this link is invalid.');
        return;
      }

      // ── Not logged in: save params and send to login ─────────────────────
      if (!authenticated || (!token && !apiKey)) {
        sessionStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify(params));
        // Pass return path so both magic-link and KC PKCE flows can bounce back.
        navigate('/login?return=%2Foauth%2Fauthorize', { replace: true });
        return;
      }

      // ── Logged in: exchange credentials for a short-lived code ───────────
      // Clean up pending state now that we have valid params + auth.
      sessionStorage.removeItem(OAUTH_PENDING_KEY);

      // Prefer API key if available; fall back to JWT token.
      // The backend auto-provisions an API key if authenticated via JWT.
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (apiKey) {
          headers['X-API-Key'] = apiKey;
        } else if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const resp = await fetch(`${API_URL}/v2/oauth/code`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ redirect_uri: params.redirectUri, state: params.state }),
        });

        if (!resp.ok) {
          const detail = (await resp.json().catch(() => ({}))) as { message?: string };
          throw new Error(detail.message ?? `Server error ${resp.status}`);
        }

        const { code } = (await resp.json()) as { code: string };

        const location = new URL(params.redirectUri);
        location.searchParams.set('code', code);
        if (params.state) location.searchParams.set('state', params.state);

        window.location.href = location.toString();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to authorize. Please try again.');
      }
    })();
  }, [authenticated, loading, apiKey, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[rgb(5,5,5)] p-4">
        <Card className="w-full max-w-md bg-zinc-800 border-zinc-700">
          <CardHeader className="text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
            <CardTitle className="text-white">Authorization Failed</CardTitle>
            <CardDescription className="text-zinc-400">{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-indigo-400 hover:text-indigo-300"
            >
              Go to Dashboard
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[rgb(5,5,5)] p-4">
      <Card className="w-full max-w-md bg-zinc-800 border-zinc-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3"><TrafficLightLogo size="md" /></div>
          <CardTitle className="text-white">Connecting to Claude</CardTitle>
          <CardDescription className="text-zinc-400 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4 text-green-400" />
            Authorizing your account…
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-4">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
        </CardContent>
      </Card>
    </div>
  );
}
